import { supabase } from '../../lib/supabase';
import type { CRMActivity, CRMContact, CRMAccount } from '../../types/crm';
import type { CRMNote } from '../../types/notes';
import type { CRMState } from './types';
import type { Database, Json } from '../../types/supabase';

type Set = (partial: Partial<CRMState> | ((state: CRMState) => Partial<CRMState>)) => void;
type Get = () => CRMState;

type ActivityRow = Database['public']['Tables']['crm_activities']['Row'];
type ContactUpdate = Database['public']['Tables']['crm_contacts']['Update'];
type AccountUpdate = Database['public']['Tables']['crm_accounts']['Update'];

/** Normalize a `crm_activities` DB row into the domain `CRMActivity`. */
function toCRMActivity(row: ActivityRow): CRMActivity {
  return {
    id: row.id,
    type: row.type as CRMActivity['type'],
    title: row.title,
    description: row.description,
    related_to_type: row.related_to_type as CRMActivity['related_to_type'],
    related_to_id: row.related_to_id,
    performed_by: row.performed_by,
    performed_at: row.performed_at,
    created_at: row.created_at,
  };
}

export const createSupportSlice = (set: Set, get: Get) => ({
  activities: [] as CRMActivity[],
  opportunityNotes: [] as CRMNote[],

  fetchActivities: async (relatedToType: string, relatedToId: string) => {
    try {
      const { data, error } = await supabase
        .from('crm_activities')
        .select('*')
        .eq('related_to_type', relatedToType)
        .eq('related_to_id', relatedToId)
        .order('performed_at', { ascending: false });

      if (error) throw error;
      set({ activities: (data ?? []).map(toCRMActivity) });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch activities';
      set({ error: message });
    }
  },

  fetchNotesForOpportunity: async (opportunityId: string, accountId?: string | null) => {
    try {
      // Build OR clauses: notes linked to opportunity, account, or contacts of account
      const orParts = [`opportunity_id.eq.${opportunityId}`];

      if (accountId) {
        orParts.push(`account_id.eq.${accountId}`);

        const { data: contacts } = await supabase
          .from('crm_contacts')
          .select('id')
          .eq('account_id', accountId)
          .eq('status', 'active');
        if (contacts && contacts.length > 0) {
          const contactIds = contacts.map(c => c.id);
          orParts.push(`contact_id.in.(${contactIds.join(',')})`);
        }
      }

      const { data, error } = await supabase
        .from('crm_notes')
        .select('*, contact:crm_contacts(id, first_name, last_name)')
        .or(orParts.join(','))
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      set({ opportunityNotes: (data as CRMNote[]) || [] });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch notes for opportunity';
      set({ error: message });
    }
  },

  createActivity: async (activity: Omit<CRMActivity, 'id' | 'created_at'>) => {
    try {
      const { error } = await supabase
        .from('crm_activities')
        .insert(activity);

      if (error) throw error;
      await get().fetchActivities(activity.related_to_type, activity.related_to_id);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create activity';
      set({ error: message });
    }
  },

  subscribeContactsToNewsletter: async (contactIds: string[]) => {
    try {
      const { data: contacts, error: contactError } = await supabase
        .from('crm_contacts')
        .select('id, email, first_name, last_name')
        .in('id', contactIds);

      if (contactError) throw contactError;

      const subscribersToInsert = contacts
        .filter((c): c is typeof c & { email: string } => Boolean(c.email))
        .map(c => ({
          email: c.email,
          first_name: c.first_name,
          last_name: c.last_name,
          crm_contact_id: c.id,
          confirmed: true,
        }));

      const { error: insertError } = await supabase
        .from('newsletter_subscribers')
        .upsert(subscribersToInsert, { onConflict: 'email' });

      if (insertError) throw insertError;

      return {
        success: true,
        message: `${subscribersToInsert.length} contact(s) inscrit(s) à la newsletter`,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to subscribe contacts';
      return { success: false, message };
    }
  },

  subscribeAccountContactsToNewsletter: async (accountId: string) => {
    try {
      const { data: contacts, error: contactError } = await supabase
        .from('crm_contacts')
        .select('id, email, first_name, last_name, account_id')
        .eq('account_id', accountId)
        .eq('status', 'active');

      if (contactError) throw contactError;

      const subscribersToInsert = contacts
        .filter((c): c is typeof c & { email: string } => Boolean(c.email))
        .map(c => ({
          email: c.email,
          first_name: c.first_name,
          last_name: c.last_name,
          crm_contact_id: c.id,
          crm_account_id: accountId,
          confirmed: true,
        }));

      const { error: insertError } = await supabase
        .from('newsletter_subscribers')
        .upsert(subscribersToInsert, { onConflict: 'email' });

      if (insertError) throw insertError;

      return {
        success: true,
        message: `${subscribersToInsert.length} contact(s) de cette société inscrits à la newsletter`,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to subscribe account contacts';
      return { success: false, message };
    }
  },

  subscribeLeadsToNewsletter: async (leadIds: string[]) => {
    try {
      const { data: leads, error: leadError } = await supabase
        .from('onboarding_leads')
        .select('id, contact_email, contact_first_name, contact_last_name')
        .in('id', leadIds);

      if (leadError) throw leadError;

      const subscribersToInsert = leads
        .filter(l => l.contact_email)
        .map(l => ({
          email: l.contact_email.toLowerCase(),
          first_name: l.contact_first_name,
          last_name: l.contact_last_name,
          confirmed: true,
        }));

      if (subscribersToInsert.length === 0) {
        return { success: false, message: 'Aucun lead avec email trouvé' };
      }

      const { error: insertError } = await supabase
        .from('newsletter_subscribers')
        .upsert(subscribersToInsert, { onConflict: 'email' });

      if (insertError) throw insertError;

      return {
        success: true,
        message: `${subscribersToInsert.length} lead(s) inscrit(s) à la newsletter`,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to subscribe leads';
      return { success: false, message };
    }
  },

  syncCompaniesFromApi: async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        return { success: false, message: 'Non authentifie' };
      }

      const proxyBaseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-proxy`;
      const authHeaders = {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      };

      const companies: Record<string, unknown>[] = [];
      const limit = 200;
      let offset = 0;

      while (true) {
        const proxyResponse = await fetch(
          `${proxyBaseUrl}/companies?limit=${limit}&offset=${offset}`,
          { headers: authHeaders }
        );

        if (!proxyResponse.ok) {
          const errorText = await proxyResponse.text();
          return { success: false, message: errorText || 'Erreur proxy CRM' };
        }

        const proxyData = await proxyResponse.json();
        const page = Array.isArray(proxyData?.data) ? proxyData.data : [];
        companies.push(...page);

        if (page.length < limit) {
          break;
        }
        offset += limit;
      }

      if (companies.length === 0) {
        return { success: false, message: 'Aucune entreprise a synchroniser' };
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/companies-sync`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ source: 'crm-api', companies }),
        }
      );

      const result = await response.json();
      if (!result.success) {
        return { success: false, message: result.error || 'Erreur de synchronisation' };
      }

      await get().fetchAccounts();
      return {
        success: true,
        message: result.message || `${result.created || 0} societes importees`,
        created: result.created,
        updated: result.updated,
        skipped: result.skipped,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur de synchronisation';
      console.error('Companies sync error:', error);
      return { success: false, message };
    }
  },

  mergeContacts: async (masterId: string, duplicateIds: string[], mergedFields: Partial<CRMContact>) => {
    set({ loading: true });
    try {
      // 1. Reassign opportunity contacts
      for (const dupId of duplicateIds) {
        const { data: masterLinks } = await supabase
          .from('crm_opportunity_contacts')
          .select('opportunity_id')
          .eq('contact_id', masterId);
        const masterOppIds = new Set((masterLinks || []).map((l) => l.opportunity_id));

        const { data: dupLinks } = await supabase
          .from('crm_opportunity_contacts')
          .select('opportunity_id')
          .eq('contact_id', dupId);

        // crm_opportunity_contacts has a composite key (contact_id, opportunity_id)
        // and no surrogate `id`; target rows by that pair.
        for (const link of dupLinks || []) {
          if (masterOppIds.has(link.opportunity_id)) {
            await supabase
              .from('crm_opportunity_contacts')
              .delete()
              .eq('contact_id', dupId)
              .eq('opportunity_id', link.opportunity_id);
          } else {
            await supabase
              .from('crm_opportunity_contacts')
              .update({ contact_id: masterId })
              .eq('contact_id', dupId)
              .eq('opportunity_id', link.opportunity_id);
          }
        }
      }

      // 2. Reassign activities
      await supabase
        .from('crm_activities')
        .update({ related_to_id: masterId })
        .eq('related_to_type', 'contact')
        .in('related_to_id', duplicateIds);

      // 3. Reassign tasks
      await supabase
        .from('crm_tasks')
        .update({ related_to_id: masterId })
        .eq('related_to_type', 'contact')
        .in('related_to_id', duplicateIds);

      // 4. Reassign newsletter subscribers
      const { data: masterSub } = await supabase
        .from('newsletter_subscribers')
        .select('id')
        .eq('crm_contact_id', masterId)
        .maybeSingle();

      if (masterSub) {
        await supabase
          .from('newsletter_subscribers')
          .delete()
          .in('crm_contact_id', duplicateIds);
      } else {
        const { data: dupSub } = await supabase
          .from('newsletter_subscribers')
          .select('id')
          .in('crm_contact_id', duplicateIds)
          .limit(1)
          .maybeSingle();

        if (dupSub) {
          await supabase
            .from('newsletter_subscribers')
            .update({ crm_contact_id: masterId })
            .eq('id', dupSub.id);
          await supabase
            .from('newsletter_subscribers')
            .delete()
            .in('crm_contact_id', duplicateIds)
            .neq('id', dupSub.id);
        }
      }

      // 5. Reassign notes (crm_notes)
      await supabase
        .from('crm_notes')
        .update({ contact_id: masterId })
        .in('contact_id', duplicateIds);

      // 5b. Merge multi-value tables (emails, phones, accounts)
      const { data: dupEmails } = await supabase
        .from('contact_emails').select('email').in('contact_id', duplicateIds);
      const { data: masterEmails } = await supabase
        .from('contact_emails').select('email').eq('contact_id', masterId);
      const masterEmailSet = new Set((masterEmails || []).map(e => e.email.toLowerCase()));
      for (const de of dupEmails || []) {
        if (!masterEmailSet.has(de.email.toLowerCase())) {
          await supabase.from('contact_emails').insert({
            contact_id: masterId, email: de.email, label: 'pro', is_primary: false,
          });
        }
      }
      await supabase.from('contact_emails').delete().in('contact_id', duplicateIds);

      // Transfer phones
      const { data: dupPhones } = await supabase
        .from('contact_phones').select('phone').in('contact_id', duplicateIds);
      const { data: masterPhones } = await supabase
        .from('contact_phones').select('phone').eq('contact_id', masterId);
      const masterPhoneSet = new Set((masterPhones || []).map(p => p.phone));
      for (const dp of dupPhones || []) {
        if (!masterPhoneSet.has(dp.phone)) {
          await supabase.from('contact_phones').insert({
            contact_id: masterId, phone: dp.phone, label: 'mobile', is_primary: false,
          });
        }
      }
      await supabase.from('contact_phones').delete().in('contact_id', duplicateIds);

      // Transfer account links
      const { data: dupAccLinks } = await supabase
        .from('contact_accounts').select('account_id').in('contact_id', duplicateIds);
      const { data: masterAccLinks } = await supabase
        .from('contact_accounts').select('account_id').eq('contact_id', masterId);
      const masterAccSet = new Set((masterAccLinks || []).map(a => a.account_id));
      for (const da of dupAccLinks || []) {
        if (!masterAccSet.has(da.account_id)) {
          await supabase.from('contact_accounts').insert({
            contact_id: masterId, account_id: da.account_id, is_primary: false,
          });
        }
      }
      await supabase.from('contact_accounts').delete().in('contact_id', duplicateIds);

      // 5c. Reassign appointments
      await supabase
        .from('appointments')
        .update({ contact_id: masterId })
        .in('contact_id', duplicateIds);

      // 5d. Reassign meeting participants
      for (const dupId of duplicateIds) {
        const { data: masterParts } = await supabase
          .from('crm_meeting_participants')
          .select('meeting_id')
          .eq('contact_id', masterId) as { data: { meeting_id: string }[] | null };
        const masterMeetingIds = new Set((masterParts || []).map(p => p.meeting_id));

        const { data: dupParts } = await supabase
          .from('crm_meeting_participants')
          .select('id, meeting_id')
          .eq('contact_id', dupId) as { data: { id: string; meeting_id: string }[] | null };

        for (const part of dupParts || []) {
          if (masterMeetingIds.has(part.meeting_id)) {
            await supabase.from('crm_meeting_participants').delete().eq('id', part.id);
          } else {
            await (supabase.from('crm_meeting_participants') as any).update({ contact_id: masterId }).eq('id', part.id);
          }
        }
      }

      // 5e. Reassign feature interests
      for (const dupId of duplicateIds) {
        const { data: masterInterests } = await supabase
          .from('contact_feature_interests')
          .select('feature_id')
          .eq('contact_id', masterId) as { data: { feature_id: string }[] | null };
        const masterFeatureIds = new Set((masterInterests || []).map(i => i.feature_id));

        const { data: dupInterests } = await supabase
          .from('contact_feature_interests')
          .select('id, feature_id')
          .eq('contact_id', dupId) as { data: { id: string; feature_id: string }[] | null };

        for (const interest of dupInterests || []) {
          if (masterFeatureIds.has(interest.feature_id)) {
            await supabase.from('contact_feature_interests').delete().eq('id', interest.id);
          } else {
            await (supabase.from('contact_feature_interests') as any).update({ contact_id: masterId }).eq('id', interest.id);
          }
        }
      }

      // 5f. Reassign task relations
      try {
        for (const dupId of duplicateIds) {
          const { data: masterRels } = await supabase
            .from('crm_task_relations')
            .select('task_id')
            .eq('relation_type', 'contact')
            .eq('relation_id', masterId) as { data: { task_id: string }[] | null };
          const masterTaskIds = new Set((masterRels || []).map(r => r.task_id));

          const { data: dupRels } = await supabase
            .from('crm_task_relations')
            .select('id, task_id')
            .eq('relation_type', 'contact')
            .eq('relation_id', dupId) as { data: { id: string; task_id: string }[] | null };

          for (const rel of dupRels || []) {
            if (masterTaskIds.has(rel.task_id)) {
              await supabase.from('crm_task_relations').delete().eq('id', rel.id);
            } else {
              await (supabase.from('crm_task_relations') as any).update({ relation_id: masterId }).eq('id', rel.id);
            }
          }
        }
      } catch { /* table may not exist */ }

      // 5g. Event registrations: one per event on the master (SQL, atomic). Without it the delete
      // below would cascade the duplicates' registrations away (PLAN-EVENEMENTS.md).
      const { error: eventsMergeError } = await supabase.rpc('merge_contact_event_registrations', {
        p_master: masterId, p_duplicates: duplicateIds,
      });
      if (eventsMergeError) throw eventsMergeError;

      // 6. Update master with merged fields
      // Strip relation-only fields and normalize custom_fields to the DB Json type.
      const { emails, phones, contact_accounts, custom_fields, ...contactRest } = mergedFields;
      void emails; void phones; void contact_accounts;
      const contactPayload: ContactUpdate = {
        ...contactRest,
        ...(custom_fields !== undefined ? { custom_fields: custom_fields as Json } : {}),
        updated_at: new Date().toISOString(),
      };
      const { error: updateError } = await supabase
        .from('crm_contacts')
        .update(contactPayload)
        .eq('id', masterId);

      if (updateError) throw updateError;

      // 7. Delete duplicate contacts
      const { error: deleteError } = await supabase
        .from('crm_contacts')
        .delete()
        .in('id', duplicateIds);

      if (deleteError) throw deleteError;

      // 8. Refresh
      await get().fetchContacts();
      set({ loading: false });
      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la fusion des contacts';
      set({ error: message, loading: false });
      return { success: false, error: message };
    }
  },

  mergeAccounts: async (masterId: string, duplicateIds: string[], mergedFields: Partial<CRMAccount>) => {
    set({ loading: true });
    try {
      // 0. The robot's conditions first (invitations, acceptances: legal evidence): if they cannot follow
      // the account kept, nothing else moves and no account is deleted (docs/domains/robot-conditions.md).
      const robotTerms = await supabase.rpc('robot_terms_move_account', { p_from: duplicateIds, p_to: masterId });
      if (robotTerms.error) {
        throw new Error('Les conditions du robot n\'ont pas pu suivre le compte gardé : fusion arrêtée, rien n\'a changé.');
      }

      // 1. Reassign contacts to master account
      await supabase
        .from('crm_contacts')
        .update({ account_id: masterId })
        .in('account_id', duplicateIds);

      // 2. Reassign opportunities to master account
      await supabase
        .from('crm_opportunities')
        .update({ account_id: masterId })
        .in('account_id', duplicateIds);

      // 3. Reassign activities
      await supabase
        .from('crm_activities')
        .update({ related_to_id: masterId })
        .eq('related_to_type', 'account')
        .in('related_to_id', duplicateIds);

      // 4. Reassign tasks
      await supabase
        .from('crm_tasks')
        .update({ related_to_id: masterId })
        .eq('related_to_type', 'account')
        .in('related_to_id', duplicateIds);

      // 5. Reassign newsletter subscribers
      const { data: masterSub } = await supabase
        .from('newsletter_subscribers')
        .select('id')
        .eq('crm_account_id', masterId)
        .maybeSingle();

      if (masterSub) {
        await supabase
          .from('newsletter_subscribers')
          .delete()
          .in('crm_account_id', duplicateIds);
      } else {
        const { data: dupSub } = await supabase
          .from('newsletter_subscribers')
          .select('id')
          .in('crm_account_id', duplicateIds)
          .limit(1)
          .maybeSingle();

        if (dupSub) {
          await supabase
            .from('newsletter_subscribers')
            .update({ crm_account_id: masterId })
            .eq('id', dupSub.id);
          await supabase
            .from('newsletter_subscribers')
            .delete()
            .in('crm_account_id', duplicateIds)
            .neq('id', dupSub.id);
        }
      }

      // 6. Reassign appointments
      await supabase
        .from('appointments')
        .update({ account_id: masterId })
        .in('account_id', duplicateIds);

      // 7. Reassign meetings
      await supabase
        .from('crm_meetings')
        .update({ account_id: masterId })
        .in('account_id', duplicateIds);

      // 8. Reassign notes
      await (supabase.from('crm_notes') as any)
        .update({ account_id: masterId })
        .in('account_id', duplicateIds);

      // 9. Reassign contact_accounts links
      for (const dupId of duplicateIds) {
        const { data: masterLinks } = await supabase
          .from('contact_accounts')
          .select('contact_id')
          .eq('account_id', masterId);
        const masterContactIds = new Set((masterLinks || []).map((l: any) => l.contact_id));

        const { data: dupLinks } = await supabase
          .from('contact_accounts')
          .select('id, contact_id')
          .eq('account_id', dupId);

        for (const link of (dupLinks || []) as any[]) {
          if (masterContactIds.has(link.contact_id)) {
            await supabase.from('contact_accounts').delete().eq('id', link.id);
          } else {
            await (supabase.from('contact_accounts') as any).update({ account_id: masterId }).eq('id', link.id);
          }
        }
      }

      // 10. Reassign task relations
      try {
        for (const dupId of duplicateIds) {
          const { data: masterRels } = await supabase
            .from('crm_task_relations')
            .select('task_id')
            .eq('relation_type', 'account')
            .eq('relation_id', masterId) as { data: { task_id: string }[] | null };
          const masterTaskIds = new Set((masterRels || []).map(r => r.task_id));

          const { data: dupRels } = await supabase
            .from('crm_task_relations')
            .select('id, task_id')
            .eq('relation_type', 'account')
            .eq('relation_id', dupId) as { data: { id: string; task_id: string }[] | null };

          for (const rel of dupRels || []) {
            if (masterTaskIds.has(rel.task_id)) {
              await supabase.from('crm_task_relations').delete().eq('id', rel.id);
            } else {
              await (supabase.from('crm_task_relations') as any).update({ relation_id: masterId }).eq('id', rel.id);
            }
          }
        }
      } catch { /* table may not exist */ }

      // 10b. Event relays: the master relays what its duplicates relayed (SQL, atomic; without it the
      // delete below would cascade the relays and orphan their registrations' origin).
      const { error: partnersMergeError } = await supabase.rpc('merge_account_event_partners', {
        p_master: masterId, p_duplicates: duplicateIds,
      });
      if (partnersMergeError) throw partnersMergeError;

      // 11. Update master with merged fields
      // Strip fields that don't exist on the DB row (role/status and friends are
      // derived from v_account_role at fetch time) and normalize custom_fields to
      // the DB Json type.
      const {
        role: _role,
        status: _status,
        clientSource: _clientSource,
        invoiceCadence: _invoiceCadence,
        nextRenewalAt: _nextRenewalAt,
        custom_fields: accountCustomFields,
        ...accountRest
      } = mergedFields;
      void _role; void _status; void _clientSource; void _invoiceCadence; void _nextRenewalAt;
      const accountPayload: AccountUpdate = {
        ...accountRest,
        ...(accountCustomFields !== undefined ? { custom_fields: accountCustomFields as Json } : {}),
        updated_at: new Date().toISOString(),
      };
      const { error: updateError } = await supabase
        .from('crm_accounts')
        .update(accountPayload)
        .eq('id', masterId);

      if (updateError) throw updateError;

      // 12. Delete duplicate accounts
      const { error: deleteError } = await supabase
        .from('crm_accounts')
        .delete()
        .in('id', duplicateIds);

      if (deleteError) throw deleteError;

      // 13. Refresh
      await get().fetchAccounts();
      await get().fetchContacts();
      set({ loading: false });
      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la fusion des comptes';
      set({ error: message, loading: false });
      return { success: false, error: message };
    }
  },
});
